import {EmptyStatusProps, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useIsFilterActive} from '../../store/selectors/useIsFilterActive';
import {useEagGridStore} from '../../store/useEagGridStore';

type DefaultEmptyStateRendererProps = {
  emptyStatus?: Omit<EmptyStatusProps, 'variant'>;
};

type EmptyStateLayoutProps = {
  headline: string;
  subheadline?: string;
  action?: {
    title?: string;
    onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  };
  'data-testid'?: string;
};

/**
 * Shared no-data layout: a soft circular table illustration, headline, optional
 * subheadline, and an optional link-style action. Hand-rolled in the grid's own
 * visual language (12-14px, #2E3A59/#6B778C, #0052CC link) — the platform
 * EmptyStatus table variant rendered a bare icon + single text line that read
 * as unfinished inside the EagGrid card.
 */
function EmptyStateLayout(props: EmptyStateLayoutProps) {
  return (
    <div
      className="flex flex-col items-center gap-3 py-12 px-6 text-center"
      data-testid={props['data-testid'] ?? 'eagGridEmptyState'}
    >
      <span className="flex items-center justify-center w-16 h-16 rounded-full bg-[#F4F5F7]">
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#97A0AF"
          strokeWidth="1.6"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <path d="M3 9h18M9 9v11" />
        </svg>
      </span>
      <div className="flex flex-col items-center gap-1">
        <span className="text-sm font-semibold text-[#2E3A59]">{props.headline}</span>
        <Show when={isNotNilOrEmpty(props.subheadline)}>
          <span className="text-xs text-[#6B778C] max-w-[360px]">{props.subheadline}</span>
        </Show>
      </div>
      {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
      {props.action?.title && (
        <button
          type="button"
          className="text-xs font-medium hover:underline bg-transparent border-none cursor-pointer p-0"
          // fontFamily: raw <button> gets the UA button font — inherit explicitly
          // (same workaround as the ActiveFilterBar link buttons).
          style={{color: '#0052CC', fontFamily: 'inherit'}}
          onClick={props.action.onClick}
        >
          {props.action.title}
        </button>
      )}
    </div>
  );
}

export function DefaultEmptyStateRenderer(props: DefaultEmptyStateRendererProps) {
  const t = useTranslationContext();
  const isFilterActive = useIsFilterActive();
  const store = useEagGridStore();

  const handleResetFilters = () => {
    store.deselectAll();
    store.resetFilterValues();
  };

  if (isFilterActive) {
    return (
      <EmptyStateLayout
        headline={t('page.datagrid.labels.emptyDueFilter')}
        action={{
          title: t('page.datagrid.labels.clearAllFilters'),
          onClick: handleResetFilters,
        }}
      />
    );
  }

  // Consumer-supplied strings are passed through `t()` so callers may pass either
  // raw i18n keys or already-translated text. i18next returns the input unchanged
  // when no translation exists, so pre-translated strings remain intact.
  const consumerAction = props.emptyStatus?.action;

  return (
    <EmptyStateLayout
      headline={
        props.emptyStatus?.headline
          ? t(props.emptyStatus.headline)
          : t('page.datagrid.labels.emptyData')
      }
      subheadline={props.emptyStatus?.subheadline ? t(props.emptyStatus.subheadline) : undefined}
      action={
        consumerAction
          ? {
              ...consumerAction,
              title: consumerAction.title ? t(consumerAction.title) : consumerAction.title,
            }
          : undefined
      }
      data-testid={props.emptyStatus?.['data-testid']}
    />
  );
}
